import { Spacing } from '@persona-id/design-tokens';
import { useTranslation } from 'react-i18next';
import { arrowStyles } from 'shared/styles/arrow';
import { FONT_SIZES } from 'shared/styles/variables';
import styled from 'styled-components';

import IconGlobe from 'images/icons/globe.svg';
import { INQUIRY_LANGUAGES } from 'lib/constants/locales';
import { LanguageSelectorComponent } from 'lib/interfaces/ui-components';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

const LanguageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  select {
    appearance: none;
    background-color: transparent;
    color: #696a7b; // matches color in PersonaBranding
    font-size: ${FONT_SIZES.h4};

    margin: -${Spacing[2]};
    padding: ${Spacing[2]};
    padding-right: ${Spacing[4]};

    &:focus-visible {
      outline: ${({ theme }) => theme.colorPrimary} auto 1px;
    }
  }

  &::after {
    ${arrowStyles};
    height: 0;
    width: 0;

    pointer-events: none;
    right: -${Spacing[2]}; // add 8px of padding between the longest language option and the caret
  }
`;

const GlobeSpacer = styled.div`
  padding-right: 4px;

  svg {
    display: block;
  }
`;

interface LanguageSelectorProps {
  component: LanguageSelectorComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
}

const LanguageSelector = (props: LanguageSelectorProps) => {
  const { component, inquiry } = props;
  const [t, i18n] = useTranslation();

  const enabledLocales = component.attributes['enabled-locales'];

  // Don't need to render selector if only one locale is enabled.
  // Still render the div to maintain footer layout.
  if (enabledLocales.length === 1) {
    return <div data-test='ui-component__language-selector' aria-disabled={true} />;
  }

  return (
    <LanguageWrapper>
      <GlobeSpacer>
        <IconGlobe aria-hidden />
      </GlobeSpacer>
      <select
        aria-label={t('intro.language-select-label')}
        data-test='ui-component__language-selector'
        dir='ltr'
        onChange={(evt) =>
          props.actionsApi.handleLanguageChange(
            inquiry,
            i18n,
            (evt.target as HTMLSelectElement).value,
          )
        }
        defaultValue={i18n.language}
      >
        {Object.keys(INQUIRY_LANGUAGES)
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          .filter((language: keyof typeof INQUIRY_LANGUAGES) => enabledLocales.includes(language))
          .map((language) => (
            <option value={language} key={language} lang={language}>
              {INQUIRY_LANGUAGES[language as keyof typeof INQUIRY_LANGUAGES]}
            </option>
          ))}
      </select>
    </LanguageWrapper>
  );
};

export default LanguageSelector;
